import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type HomeTab =
  | 'feed'
  | 'discovery'
  | 'profile'
  | 'follows'
  | 'blocks'
  | 'activity'
  | 'ai'
  | 'saved'

export type RightPanelTab = 'pinned' | 'ai' | 'horny' | 'activity'

export type PanelBody =
  | 'content'
  | 'accounts'
  | 'settings'
  | 'activity'
  | 'pinned'
  | 'ai'

interface UIState {
  panel: PanelBody
  exploreOpen: boolean
  rightPanelOpen: boolean
  rightPanelTab: RightPanelTab
  homeTab: HomeTab
  contentCollapsed: boolean
  addAccountOpen: boolean
  activityUnseen: number
  blurAdult: boolean
  navIcons: Record<string, string>
  profileLeftOpen: boolean

  setExploreOpen: (v: boolean) => void
  toggleExplore: () => void
  setRightPanelOpen: (v: boolean) => void
  toggleRightPanel: () => void
  setRightPanelTab: (t: RightPanelTab) => void
  setHomeTab: (t: HomeTab) => void
  setPanel: (p: PanelBody) => void
  togglePanel: (p: PanelBody) => void
  setContentCollapsed: (v: boolean) => void
  toggleContentCollapsed: () => void
  setAddAccountOpen: (v: boolean) => void
  setActivityUnseen: (n: number) => void
  setBlurAdult: (v: boolean) => void
  toggleBlurAdult: () => void
  setNavIcon: (path: string, dataUrl: string) => void
  clearNavIcon: (path: string) => void
  setProfileLeftOpen: (v: boolean) => void
  toggleProfileLeft: () => void

  profileView: 'gallery' | 'list'
  setProfileView: (v: 'gallery' | 'list') => void

  /** Subscribe to Jetstream when viewing a profile for real-time new-post tiles. */
  jetstreamEnabled: boolean
  setJetstreamEnabled: (v: boolean) => void

  /** Active content sources for the profile gallery/list (multi-select). */
  profileSources: Array<'posts' | 'replies' | 'likes' | 'reposts'>
  /** Whether the likers view is active (exclusive — clears profileSources). */
  profileLikers: boolean
  toggleProfileSource: (s: 'posts' | 'replies' | 'likes' | 'reposts') => void
  setProfileLikers: (v: boolean) => void

  /** Mobile portrait top ribbon: rows 2-3 (home tabs + open-tabs strip)
   *  expanded. The chevron in row 1 toggles this; scroll auto-hide is
   *  layered on top in MobileShell and never writes here. */
  mobileTopRibbonExpanded: boolean
  /** When pinned, scrolling never auto-collapses the mobile ribbons
   *  (top rows 2-3 and the bottom nav). */
  mobileRibbonPinned: boolean
  setMobileTopRibbonExpanded: (v: boolean) => void
  toggleMobileTopRibbon: () => void
  setMobileRibbonPinned: (v: boolean) => void
  toggleMobileRibbonPinned: () => void
}

export const useUI = create<UIState>()(
  persist(
    (set, get) => ({
      panel: 'content',
      exploreOpen: true,
      rightPanelOpen: true,
      rightPanelTab: 'pinned',
      homeTab: 'feed',
      contentCollapsed: false,
      addAccountOpen: false,
      activityUnseen: 0,
      blurAdult: false,
      navIcons: {},
      profileLeftOpen: true,

      setExploreOpen: (v) => set({ exploreOpen: v }),
      toggleExplore: () => set({ exploreOpen: !get().exploreOpen }),
      setRightPanelOpen: (v) => set({ rightPanelOpen: v }),
      toggleRightPanel: () => set({ rightPanelOpen: !get().rightPanelOpen }),
      setRightPanelTab: (t) => set({ rightPanelTab: t }),
      setHomeTab: (t) => set({ homeTab: t }),
      setPanel: (p) => set({ panel: p }),
      togglePanel: (p) => set({ panel: get().panel === p ? 'content' : p }),
      setContentCollapsed: (v) => set({ contentCollapsed: v }),
      toggleContentCollapsed: () =>
        set({ contentCollapsed: !get().contentCollapsed }),
      setAddAccountOpen: (v) => set({ addAccountOpen: v }),
      setActivityUnseen: (n) => set({ activityUnseen: n }),
      setBlurAdult: (v) => set({ blurAdult: v }),
      toggleBlurAdult: () => set({ blurAdult: !get().blurAdult }),
      setNavIcon: (path, dataUrl) =>
        set({ navIcons: { ...get().navIcons, [path]: dataUrl } }),
      clearNavIcon: (path) => {
        const next = { ...get().navIcons }
        delete next[path]
        set({ navIcons: next })
      },
      setProfileLeftOpen: (v) => set({ profileLeftOpen: v }),
      toggleProfileLeft: () =>
        set({ profileLeftOpen: !get().profileLeftOpen }),

      profileView: 'gallery',
      setProfileView: (v) => set({ profileView: v }),

      jetstreamEnabled: true,
      setJetstreamEnabled: (v) => set({ jetstreamEnabled: v }),

      profileSources: ['posts'],
      profileLikers: false,
      toggleProfileSource: (s) => {
        const cur = get().profileSources
        const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]
        // Ensure at least one source stays selected.
        set({ profileSources: next.length > 0 ? next : [s], profileLikers: false })
      },
      setProfileLikers: (v) => set({ profileLikers: v, profileSources: v ? [] : ['posts'] }),

      mobileTopRibbonExpanded: true,
      mobileRibbonPinned: false,
      setMobileTopRibbonExpanded: (v) => set({ mobileTopRibbonExpanded: v }),
      toggleMobileTopRibbon: () =>
        set({ mobileTopRibbonExpanded: !get().mobileTopRibbonExpanded }),
      setMobileRibbonPinned: (v) => set({ mobileRibbonPinned: v }),
      toggleMobileRibbonPinned: () =>
        set({ mobileRibbonPinned: !get().mobileRibbonPinned }),
    }),
    {
      name: 'bskyhq:ui',
      version: 7,
      partialize: ({ contentCollapsed: _c, addAccountOpen: _a, ...rest }) => rest,
      migrate: (persisted, fromVersion) => {
        const p = persisted as Record<string, unknown>
        if (fromVersion < 3 && p?.rightPanelTab === 'combo') p.rightPanelTab = 'activity'
        if (fromVersion < 4) {
          if (p?.homeTab === 'follows') p.homeTab = 'feed'
          if (p?.homeTab === 'feeds') p.homeTab = 'saved'
        }
        if (fromVersion < 5) {
          if (!p?.navIcons || typeof p.navIcons !== 'object') p.navIcons = {}
        }
        if (fromVersion < 6) {
          p.panel = 'content'
        }
        if (fromVersion < 7) {
          p.jetstreamEnabled = true
        }
        return p as unknown as UIState
      },
    },
  ),
)
